import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { Box, Grid } from "@mui/material";
import SentinelSidebarView from "app/components/flow/designer/sentinel/sentinel-sidebar";
import { Node } from "reactflow";
import { sxStyles } from "./sidebar.styles";
import {
    createAutoProperties,
    createMetaObject,
    createGraphNodeModel,
    renderSidebarView,
    cleanupSidebarView
} from "./sidebar.utils";
import { FormTypes } from "../../flow/flow.constants";
import FormTitle from "./form-title";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getIsSidebarFullWidth } from "../../helpers/selectors";
import {
    setIsRightSidebarOpenAction,
    setSelectedNodeAction,
    setIsSentinelLiveMonitorSidebarOpenAction,
    setSentinelLiveMonitorDefaultSentinelAction,
    setIsSidebarFullWidthAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";

type SentinelSidebarProps = {
    selectedNode: Node | null;
    flowId: string;
    app: any;
    appStatus: string | null;
    onSidebarSave?: (data: any) => void;
    onSidebarCancel?: () => void;
    onDelete?: () => void;
    formType?: keyof typeof FormTypes;
    toggleFormType?: () => void;
};

export interface SentinelSidebarWrapperRef {
    isDirty: () => boolean;
}

const SentinelSidebarWrapper = forwardRef<SentinelSidebarWrapperRef, SentinelSidebarProps>(
    (
        { selectedNode, flowId, app, appStatus, onSidebarSave, onSidebarCancel, onDelete, formType, toggleFormType },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const sidebarViewRef = useRef<any>(null);
        const dispatch = useAppDispatch();
        const isSidebarFullWidth = useAppSelector(getIsSidebarFullWidth);

        // Expose isDirty method to parent component
        useImperativeHandle(ref, () => ({
            isDirty: () => {
                if (!sidebarViewRef.current) {
                    return false;
                }

                // For SENTINEL nodes, check the _isDirty property set by the isDirty event
                if (sidebarViewRef.current._isDirty !== undefined) {
                    return sidebarViewRef.current._isDirty;
                }

                return false;
            }
        }));

        // Watch for app status changes and update the sidebar without recreating it
        useEffect(() => {
            if (sidebarViewRef.current && appStatus && app) {
                app.flowStatus = appStatus;
                sidebarViewRef.current.setAppStatus(appStatus);
            }
        }, [appStatus, app]);

        // Sync full-width state → panel's isExpanded prop
        useEffect(() => {
            if (sidebarViewRef.current) {
                sidebarViewRef.current.setIsExpanded(isSidebarFullWidth);
            }
        }, [isSidebarFullWidth]);

        useEffect(() => {
            if (!selectedNode || !containerRef.current) {
                return;
            }

            // Create auto properties for new components only
            const isNewComponent = selectedNode.data?.metaObjectId === "temp-node" || !selectedNode.data?.metaObjectId;
            const autoProperties = isNewComponent ? createAutoProperties(null, null, null) : {};

            // Create meta object from React Flow node
            const metaObject = createMetaObject(selectedNode, app, autoProperties);

            // Create Backbone GraphNodeModel
            const graphNodeModel = createGraphNodeModel(selectedNode, metaObject, null, flowId, app);

            // Pass entityDataFromSource so SentinelPanel pre-populates entities from Discover PII
            if (selectedNode.data?.entityDataFromSource) {
                (graphNodeModel as any).entityDataFromSource = selectedNode.data.entityDataFromSource;
            }
            // Pass parentStream so SentinelPanel pre-fills inputStream with the source's output stream.
            // Prefer autoProperties.inputStream (set when dropped/connected to a stream) with fallback
            // to the legacy parentStream field on node data.
            const parentStream = metaObject?.autoProperties?.inputStream ?? selectedNode.data?.parentStream;
            if (parentStream) {
                (graphNodeModel as any).parentStream = parentStream;
            }

            // Create SENTINEL sidebar view
            const sidebarView = new SentinelSidebarView({
                appModel: app,
                nodeModel: graphNodeModel,
                onViewActivityClick: (sentinelFQN: string) => {
                    dispatch(setSentinelLiveMonitorDefaultSentinelAction(sentinelFQN));
                    dispatch(setIsRightSidebarOpenAction(false));
                    dispatch(setSelectedNodeAction(null));
                    dispatch(setIsSentinelLiveMonitorSidebarOpenAction(true));
                }
            });

            // SENTINEL uses different event names than regular sidebar
            if (onSidebarSave) {
                sidebarView.on("sentinel-save", onSidebarSave);
            }
            if (onSidebarCancel) {
                sidebarView.on("cancel-sentinel", onSidebarCancel);
            }
            if (onDelete) {
                sidebarView.on("delete-sentinel", onDelete);
            }
            // Track isDirty state for SENTINEL
            sidebarView.on("isDirty", (isDirty: boolean) => {
                // Store dirty state on the view for later access
                (sidebarView as any)._isDirty = isDirty;
            });

            // Render the sidebar view
            renderSidebarView(sidebarView, containerRef.current);

            // Store reference for cleanup
            sidebarViewRef.current = sidebarView;

            // Cleanup function
            return () => {
                dispatch(setIsSidebarFullWidthAction(false));
                const viewToCleanup = sidebarViewRef.current;
                sidebarViewRef.current = null;
                setTimeout(() => {
                    cleanupSidebarView(viewToCleanup);
                }, 0);
            };
        }, [selectedNode, flowId, app?.id]); // Only recreate when node, flow, or app ID changes

        if (!app) return null;

        if (formType === FormTypes.MODAL) return <Box ref={containerRef} sx={sxStyles.sidebar(formType)} />;
        else
            return (
                <Grid height={"calc(100% - 80px)"} sx={{ backgroundColor: "white" }}>
                    <FormTitle node={selectedNode} type={formType} toggleFormType={toggleFormType} />
                    <Box ref={containerRef} sx={sxStyles.sidebar(formType)} />
                </Grid>
            );
    }
);

export default SentinelSidebarWrapper;
